import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Package, Plus, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import {
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { PageHeader } from "@/components/ui/headers"
import { InlineInput } from "@/components/ui/inline-input"
import { InlineSelect } from "@/components/ui/inline-select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { MovementsTable } from "@/features/staff/inventory/movements-table"
import { ShortageSummary } from "@/features/staff/inventory/shortage-summary"
import { SupplyForm } from "@/features/staff/inventory/supply-form"
import { PURCHASING_PATHS } from "@/features/staff/purchasing/purchasing"
import {
  INVENTORY_PATHS,
  tInventory,
  type ShortageState,
  type Supply,
  type SupplyDetail,
  type UnitOption,
} from "@/features/staff/inventory/inventory"
import { t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"

export type SuppliesWorkspaceProps = {
  supplies: Supply[]
  units: UnitOption[]
  shortage: ShortageState
  /** Absent when nothing is pending, or when `purchasing` is not enabled here. */
  pendingPurchases?: { count: number } | null
  /** Filled by the partial reload the ledger drawer fires; absent until then. */
  ledger?: SupplyDetail | null
  timeZone?: string | null
}

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.supplies.index"

function SupplyRow({
  supply,
  units,
  onOpenLedger,
  onDelete,
}: {
  supply: Supply
  units: UnitOption[]
  onOpenLedger: () => void
  onDelete: () => void
}) {
  return (
    <TableRow data-testid={`supply-row-${supply.id}`}>
      <TableCell className="min-w-48">
        <InlineInput
          value={supply.name}
          aria-label={tInventory("name_label")}
          successMessage={tInventory("supply_updated")}
          data-testid={`supply-${supply.id}-name`}
          onSubmit={(value) => patchInline(INVENTORY_PATHS.supply(supply.id), { name: value })}
        />
      </TableCell>

      {/* Locked rather than hidden once movements exist: the manager needs to see
          what the unit IS, and the disabled control plus its sentence is what
          explains why it cannot change. A missing field reads as a bug. */}
      <TableCell className="min-w-36">
        <InlineSelect
          value={supply.unit}
          disabled={supply.unitLocked}
          options={units.map((option) => ({
            value: option.value,
            label: option.label,
            testId: `supply-${supply.id}-unit-option-${option.value}`,
          }))}
          className="w-full"
          successMessage={tInventory("supply_updated")}
          data-testid={`supply-${supply.id}-unit`}
          onSubmit={(value) => patchInline(INVENTORY_PATHS.supply(supply.id), { unit: value })}
        />
      </TableCell>

      <TableCell className="min-w-36">
        <InlineInput
          value={supply.reorderPoint ?? ""}
          inputMode="decimal"
          aria-label={tInventory("reorder_point_label")}
          placeholder={tInventory("reorder_point_placeholder")}
          successMessage={tInventory("supply_updated")}
          data-testid={`supply-${supply.id}-reorder-point`}
          onSubmit={(value) =>
            patchInline(INVENTORY_PATHS.supply(supply.id), { reorder_point: value })
          }
        />
      </TableCell>

      <TableCell
        className="text-foreground whitespace-nowrap text-right font-semibold tabular-nums"
        data-testid={`supply-${supply.id}-stock`}
      >
        {supply.stockOnHand} {supply.unitLabel}
      </TableCell>

      <TableCell>
        <span className="flex items-center justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onOpenLedger}
            data-testid={`supply-${supply.id}-ledger`}
          >
            {tInventory("ledger_open")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={!supply.destroyable}
            onClick={onDelete}
            aria-label={tInventory("delete")}
            title={supply.destroyable ? undefined : tInventory("delete_blocked")}
            data-testid={`supply-${supply.id}-delete`}
          >
            <Trash2 className="size-4" />
          </Button>
        </span>
      </TableCell>
    </TableRow>
  )
}

/**
 * The sentence that answers "registré mis compras y el inventario no se movió".
 *
 * It is not a bug and it is not a delay: `Location::Purchase#receive!` is the
 * only thing that moves stock, because a purchase that has been written down is
 * an expectation and an expectation must not move a balance. What was missing is
 * anyone saying so on the screen where the balance is read.
 */
function PendingPurchasesNotice({ count }: { count: number }) {
  return (
    <Card density="compact" data-testid="supplies-pending-purchases">
      <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm">
          <span className="font-medium">{tInventory("pending_purchases_title", { count })}</span>{" "}
          <span className="text-muted-foreground">{tInventory("pending_purchases_hint")}</span>
        </p>
        <Button asChild variant="outline" size="sm" data-testid="supplies-pending-purchases-link">
          <Link href={PURCHASING_PATHS.purchases}>{tInventory("pending_purchases_action")}</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * The pantry: what the kitchen buys, how much of it there is, and what moved it.
 *
 * ## The shortage block is not a summary of this list
 *
 * `ShortageSummary` owns the one decision that makes this screen honest, and it
 * is deliberately above the list rather than woven into it: a restaurant with no
 * recipes gets a setup prompt and **no shortage figure anywhere**, because
 * nothing draws stock down for it and every shortage the rows could imply is a
 * data-entry mistake. The rows below stay plain readings either way — a balance
 * and a unit — so nothing on this screen can accidentally start signalling
 * "short" behind the gate's back.
 *
 * ## Everything a manager changes mid-service commits inline
 *
 * A rename, a reorder point, a unit on a supply that has not moved yet: all
 * light operational fields, all `InlineInput`/`InlineSelect`. Creating a supply
 * keeps an explicit submit, because the unit chosen there is close to permanent.
 *
 * ## The delete button is withheld, not offered and then refused
 *
 * `destroyable` arrives answered. A supply with movements or with a recipe line
 * cannot be destroyed — `dependent: :restrict_with_error` on both — and offering
 * a control that always fails is telling a manager to try something the system
 * already knew would not work.
 */
export function SuppliesWorkspace({
  supplies,
  units,
  shortage,
  pendingPurchases,
  ledger,
  timeZone = null,
}: SuppliesWorkspaceProps) {
  const [creating, setCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Supply | null>(null)
  const [ledgerSupply, setLedgerSupply] = useState<Supply | null>(null)

  const confirmDelete = () => {
    if (!pendingDelete) return

    router.delete(INVENTORY_PATHS.supply(pendingDelete.id), {
      preserveScroll: true,
      onFinish: () => setPendingDelete(null),
    })
  }

  /**
   * The drawer asks this same controller for the ledger instead of a JSON
   * endpoint of its own: an Inertia partial reload keeps the feature guard and
   * the tenant scope that already protect this screen, and adds no second door
   * into the data.
   */
  const openLedger = (supply: Supply) => {
    setLedgerSupply(supply)
    router.reload({ only: [ "ledger" ], data: { supply_id: supply.id } })
  }

  // `ledger` still holds the previously opened supply while the reload is in
  // flight. Rendering it under this supply's name would put one pantry item's
  // movements under another's heading, which is worse than a skeleton.
  const openLedgerData = ledgerSupply && ledger?.id === ledgerSupply.id ? ledger : null

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={t(`${PAGE_SCOPE}.title`)}
          description={t(`${PAGE_SCOPE}.description`)}
          actions={
            supplies.length > 0 ? (
              <Button type="button" onClick={() => setCreating(true)} data-testid="supply-new">
                <Plus className="size-4" />
                {tInventory("new")}
              </Button>
            ) : undefined
          }
        />

        <ShortageSummary shortage={shortage} supplies={supplies} />

        {pendingPurchases && <PendingPurchasesNotice count={pendingPurchases.count} />}

        {supplies.length === 0 ? (
          <Card data-testid="supplies-empty">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <Package className="text-muted-foreground size-8" aria-hidden="true" />
              <p className="text-sm font-semibold">{tInventory("empty_title")}</p>
              <p className="text-muted-foreground max-w-md text-sm">{tInventory("empty_hint")}</p>
              <Button type="button" onClick={() => setCreating(true)} data-testid="supply-new-empty">
                <Plus className="size-4" />
                {tInventory("empty_action")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card density="compact" data-testid="supplies-list">
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tInventory("name_label")}</TableHead>
                    <TableHead>{tInventory("unit_label")}</TableHead>
                    <TableHead>{tInventory("reorder_point_label")}</TableHead>
                    <TableHead className="text-right">{tInventory("stock_label")}</TableHead>
                    <TableHead className="text-right">{tInventory("actions_label")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplies.map((supply) => (
                    <SupplyRow
                      key={supply.id}
                      supply={supply}
                      units={units}
                      onOpenLedger={() => openLedger(supply)}
                      onDelete={() => setPendingDelete(supply)}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <ResponsiveDrawer
        open={ledgerSupply !== null}
        onOpenChange={(next) => !next && setLedgerSupply(null)}
      >
        <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-4xl">
          <DrawerHeader>
            <DrawerTitle>{ledgerSupply?.name ?? tInventory("ledger_title")}</DrawerTitle>
            <DrawerDescription>{tInventory("ledger_description")}</DrawerDescription>
          </DrawerHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4">
            <p className="text-muted-foreground text-xs" data-testid="supply-drawer-immutable">
              {tInventory("ledger_immutable")}
            </p>

            {openLedgerData === null ? (
              <div className="space-y-2" data-testid="supply-drawer-loading">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : openLedgerData.movements.length === 0 ? (
              <p className="text-muted-foreground py-6 text-center text-sm" data-testid="supply-drawer-empty">
                {tInventory("ledger_empty")}
              </p>
            ) : (
              <MovementsTable
                movements={openLedgerData.movements}
                unitLabel={openLedgerData.unitLabel}
                timeZone={timeZone}
                data-testid="supply-drawer-ledger"
              />
            )}
          </div>

          <DrawerFooter>
            {/* Adjustments live on the supply's own screen, with the dialog that
                explains what each reason means. The drawer is for reading. */}
            <Button asChild variant="outline" data-testid="supply-drawer-open-full">
              <Link href={ledgerSupply ? INVENTORY_PATHS.supply(ledgerSupply.id) : INVENTORY_PATHS.supplies}>
                {tInventory("ledger_open_full")}
              </Link>
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </ResponsiveDrawer>

      <ResponsiveDrawer open={creating} onOpenChange={setCreating}>
        <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
          <DrawerHeader>
            <DrawerTitle>{tInventory("new_title")}</DrawerTitle>
            <DrawerDescription>{tInventory("form_description")}</DrawerDescription>
          </DrawerHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            <SupplyForm units={units} onDone={() => setCreating(false)} />
          </div>
        </DrawerContent>
      </ResponsiveDrawer>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tInventory("delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{tInventory("delete_description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supply-delete-cancel">
              {tInventory("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="supply-delete-confirm">
              {tInventory("delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainContainer>
  )
}
